import {
  AlignItems,
  Body1,
  Box,
  Color,
  Display,
  Display1,
  FlexDirection,
  FontWeight,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import React from 'react'

import type { UnsubscribedHeroData } from '@library/api-customer-engagement-experience'

import { EmptyStateHeroOptionCard } from './EmptyStateHeroOptionCard'

type EmptyStateHeroProps = {
  data: UnsubscribedHeroData
  onActionClick: (intent: string) => void
}

const Headline = withCitrusTheme(Display1, () => ({
  fontWeight: 800,
  textTransform: 'uppercase',
  margin: 0,
}))

// Apply smaller padding only on mobile
const HeroContainer = withCitrusTheme(Box, () => ({
  padding: '16px',
  '@media (min-width: 480px)': {
    padding: '32px',
  },
}))

export const EmptyStateHero = ({ data, onActionClick }: EmptyStateHeroProps) => (
  <HeroContainer
    bg={Color.ColdGrey_800}
    data-testid="empty-state-hero"
    display={Display.Flex}
    flexDirection={FlexDirection.Column}
    gap={4}
    style={{ borderRadius: '4px' }}
  >
    <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2}>
      <Body1
        color={Color.White}
        fontWeight={[FontWeight.Normal, FontWeight.Bold]}
        size={[1, null, 9]}
      >
        {data.welcome}
      </Body1>
      <Headline color={Color.White} size={[11, null, 14]}>
        {data.headline}
      </Headline>
      {data.offer ? (
        <Box
          alignItems={[AlignItems.FlexStart, null, AlignItems.Center]}
          display={Display.Flex}
          flexDirection={[FlexDirection.Column, null, FlexDirection.Row]}
          gap={2}
          style={{ marginTop: '8px', marginBottom: '4px' }}
        >
          <Box alignItems={AlignItems.Center} display={Display.Flex} gap={2}>
            <Box
              alignItems={AlignItems.Center}
              bg={Color.Success_900}
              display={Display.Flex}
              paddingH={2}
              style={{ borderRadius: '4px' }}
            >
              <Text color={Color.White} fontWeight={FontWeight.Bold} size={[4, null, 9]}>
                {data.offer.badge}
              </Text>
            </Box>
            {data.offer.label ? (
              <Body1 color={Color.White} fontWeight={FontWeight.Bold} size={[4, null, 9]}>
                {data.offer.label}
              </Body1>
            ) : null}
          </Box>
          {data.offer.expiry_label ? (
            <Box alignItems={AlignItems.Center} display={Display.Flex} gap={2}>
              <Box aria-hidden display={[Display.None, null, Display.Block]}>
                <Text color={Color.ColdGrey_200} size={[2, null, 5]}>
                  •
                </Text>
              </Box>
              <Body1 color={Color.ColdGrey_200} fontWeight={FontWeight.Normal} size={[2, null, 5]}>
                {data.offer.expiry_label}
              </Body1>
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
    <Box
      display={Display.Flex}
      flexDirection={[FlexDirection.Column, null, null, FlexDirection.Row]}
      gap={[4, null, null, 3]}
    >
      {data.options.map((option) => (
        <Box flexBasis="0" flexGrow={1} key={option.action.intent}>
          <EmptyStateHeroOptionCard
            option={option}
            onClick={() => onActionClick(option.action.intent)}
          />
        </Box>
      ))}
    </Box>
  </HeroContainer>
)
